import { useSelector } from 'react-redux';

import { hasFix } from '../../../../../shared/vulns/remediation';
import * as IssuesV2Store from '../../../../redux/issuesV2';
import type { VulnerabilityIssue } from '../../../../../shared/issuesV2';
import type { UpgradeDistance } from '../../../../../shared/vulns/types';

export type FossabotFixDisplay = {
  /** Chosen fix version string: the complete fix when one exists, else the partial fix. */
  version: string;
  /** Upgrade distance of the chosen fix. Null when it could not be calculated. */
  distance: UpgradeDistance | null;
  /** True when the complete fix is the one being shown. */
  isComplete: boolean;
  /** CVE identifier of the active vulnerability. */
  cve: string;
  /**
   * Number of known vulnerabilities on this dependency that the complete fix
   * resolves, shown in the "Fixes all N known vulnerabilities" copy. Null until
   * the backend supplies it, in which case the copy omits the number.
   */
  vulnCount: number | null;
};

/**
 * Reads the active vulnerability issue from Redux and derives the fix that the
 * FossabotFixBox displays. Prefers the complete fix (via hasFix); otherwise
 * falls back to the partial fix. The chosen fix drives the displayed version,
 * the upgrade-size badge, and the fix-type badge.
 */
export const useFossabotFixDisplay = (): FossabotFixDisplay => {
  const issue = useSelector(IssuesV2Store.selectors.getIssueDetails) as VulnerabilityIssue;
  const { completeFix, completeFixDistance, partialFix, partialFixDistance } = issue.remediation;
  const isComplete = hasFix(completeFix);

  return {
    version: isComplete ? completeFix : partialFix,
    distance: (isComplete ? completeFixDistance : partialFixDistance) ?? null,
    isComplete,
    cve: issue.cve,
    /*
     * TODO: the issues API does not yet return the dependency-level count of
     * vulnerabilities the complete fix resolves. Wire this to the real value
     * once the backend supplies it; until then the copy omits the number.
     */
    vulnCount: null,
  };
};
